import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { useAppSelector } from '..';

export type ExclusiveOeds = {
  exclusive_oed: {
    type: string;
    oed_type_id: string;
    name: string;
  };
};

export type PNLD_OED = {
  oed: {
    strapi_id: number;
    type: string;
    name: string;
    created_at: string;
    updated_at: string;
    url: string;
  };
};

export type PNLD = {
  pnld: { name: string; id: string; pnld_oeds: PNLD_OED[] };
};

export type Role = {
  role: {
    id: string;
    name: string;
    permission: {
      edit: boolean;
      view: boolean;
      create: boolean;
      export: boolean;
    };
    role_oed_exclusives: ExclusiveOeds[];
    role_pnlds: PNLD[];
  };
};

export type TempUserOed = {
  limit_exports: string;
  limit_oed: string;
  limit_views: string;
};

export type Storages = {
  name: string;
  storage_oeds: {
    strapi_oed_id: number;
    active: boolean;
  };
};

export type Types = {
  type: { permission: string };
};

export type Users = {
  id: string;
  first_name: string;
  last_name: string;
  temp_user_oeds: TempUserOed[];
  user_roles: Role[];
  storages: Storages[];
  user_types: Types[];
};

export type UserData = {
  users: Users[];
  userStrapiId: number;
  email: string;
  oedTypesAvailable: string[];
};

export type UserDataState = {
  initialData: UserData;
  isLoading: boolean;
  hasError: boolean;
};

const initialState: UserDataState = {
  initialData: {} as any,

  isLoading: false,
  hasError: false,
};

export const loadUserData = createAsyncThunk(
  'payload/userdata',
  async (strap_user_id: number) => {
    //
    const userReq = fetch('/api/graphql/getUserByLogin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variables: { _eq: strap_user_id } }),
    }).then((res) => res.json());

    const groupOedsPermissionReq = axios.post<
      Array<{
        oed: {
          type: any;
          persmission: any;
        };
      }>
    >('/api/getGroup');

    const [userRes, groupOedsPermissionRes] = await Promise.all([
      userReq,
      groupOedsPermissionReq,
    ]);

    const user = userRes;
    const groupOedsPermission = groupOedsPermissionRes.data;

    const groupOedTypes = groupOedsPermission.map((item) => item.oed.type);
    const userRoles = user.data.login[0].users[0].user_roles;

    userRoles.forEach(({ role }: any) => {
      const oedsExclusive = [] as string[];
      role.role_oed_exclusives.forEach((exclusive: any) => {
        oedsExclusive.push(exclusive.exclusive_oed.type);
      });

      groupOedTypes.push(...oedsExclusive);
    });

    return {
      userData: {
        ...user.data.login[0],
        userStrapiId: strap_user_id,
        oedTypesAvailable: groupOedTypes,
      },
    };
  },
);

const userData = createSlice({
  name: 'userData',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(loadUserData.pending, (state) => {
      state.isLoading = true;
      state.hasError = false;
    });

    builder.addCase(loadUserData.rejected, (state) => {
      state.isLoading = false;
      state.hasError = true;
    });

    builder.addCase(loadUserData.fulfilled, (state, action) => {
      state.isLoading = false;
      state.initialData = action.payload.userData;
      state.hasError = false;
    });
  },
});

export const user = userData.reducer;

export const userActions = userData.actions;

export const useUserData = () => {
  return useAppSelector((state) => {
    return state.user;
  });
};
